import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Deal, DealStatus, Product, DealProduct } from "../types";
import { currency, ClientHubBadge, Button, PhoneInput } from "@repo/o37-ui";
import ActivityTimeline from "./ActivityTimeline";
import { useToast } from "@repo/o37-ui";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

type DealModalProps = {
  deal: Deal;
  partnerId: string;
  onClose: () => void;
  onSaved: () => void;
};

export default function DealModal({ deal, partnerId, onClose, onSaved }: DealModalProps) {
  const [editing, setEditing] = useState<Deal>(deal);
  const [products, setProducts] = useState<Product[]>([]);
  const [saving, setSaving] = useState(false);
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error("Failed to load products:", error);
    }
  };

  const saveDeal = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/deals/${editing.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editing),
      });

      if (response.ok) {
        onSaved();
        onClose();
        showToast("Deal saved successfully!", "success");
      } else {
        const error = await response.json();
        showToast(`Failed to save deal: ${error.error || 'Unknown error'}`, "error");
      }
    } catch (error) {
      console.error("Failed to save deal:", error);
      showToast("Failed to save deal.", "error");
    } finally {
      setSaving(false);
    }
  };

  const createClientHubAccount = async () => {
    try {
      setIsCreatingClient(true);
      setEditing({ ...editing, syncStatus: 'pending' });
      
      const response = await fetch(`${API_URL}/deals/${editing.id}/create-client`, {
        method: "POST",
      });

      if (response.ok) {
        const updatedDeal = await response.json();
        setEditing(updatedDeal);
        showToast("Client account created and linked!", "success");
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to create client");
      }
    } catch (error: any) {
      console.error("Failed to create client:", error);
      showToast(`Failed to create client: ${error.message}`, "error");
      setEditing({ ...editing, syncStatus: 'failed' });
    } finally {
      setIsCreatingClient(false);
    }
  };

    const removeProductFromDeal = (index: number) => {
    if (!editing.products) return;
    const newProducts = [...editing.products];
    newProducts.splice(index, 1);
    
    // Recalculate Deal Value
    const newDealValue = newProducts.reduce((sum, p) => sum + (p.priceAtDeal * p.quantity), 0);
    const newCommissionValue = (newDealValue * editing.commissionPercentage) / 100;

    setEditing({ ...editing, products: newProducts, dealValue: newDealValue, commissionValue: newCommissionValue });
  };

  const updateDealProduct = (index: number, field: keyof DealProduct, value: number) => {
    if (!editing.products) return;
    const newProducts = [...editing.products];
    newProducts[index] = { ...newProducts[index], [field]: value };
    
    // Recalculate Deal Value
    const newDealValue = newProducts.reduce((sum, p) => sum + (p.priceAtDeal * p.quantity), 0);
    const newCommissionValue = (newDealValue * editing.commissionPercentage) / 100;

    setEditing({ ...editing, products: newProducts, dealValue: newDealValue, commissionValue: newCommissionValue });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 overflow-y-auto py-10">
      <div className="w-full max-w-4xl bg-white rounded-2xl border border-neutral-200 p-6 shadow-xl my-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-semibold">{editing.name}</div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-900 p-2">✕</button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[13px]">
          {/* Basic Info */}
          <div className="space-y-3">
            <h4 className="font-semibold text-neutral-900 border-b pb-1">Deal Info</h4>
            <label className="flex flex-col gap-1">
              <span className="text-neutral-600">Company</span>
              <input className="border rounded-xl px-3 py-2" value={editing.company || ""} onChange={(e) => setEditing({ ...editing, company: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-neutral-600">Contact Name</span>
              <input className="border rounded-xl px-3 py-2" value={editing.contactName || ""} onChange={(e) => setEditing({ ...editing, contactName: e.target.value })} />
            </label>
            <div className="grid grid-cols-2 gap-2">
               <label className="flex flex-col gap-1">
                <span className="text-neutral-600">Email</span>
                <input className="border rounded-xl px-3 py-2" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-neutral-600">Phone</span>
                <PhoneInput 
                  value={editing.phone || ""} 
                  onChange={(val) => setEditing({ ...editing, phone: val || "" })}
                  className="rounded-xl"
                />
              </label>
            </div>
          </div>

          {/* Status & Value */}
          <div className="space-y-3">
            <h4 className="font-semibold text-neutral-900 border-b pb-1">Status & Value</h4>
            <label className="flex flex-col gap-1">
              <span className="text-neutral-600">Status</span>
              <select 
                className="border rounded-xl px-3 py-2" 
                value={editing.status} 
                onChange={(e) => {
                  const newStatus = e.target.value as DealStatus;
                  let newProb = editing.closeProbability;
                  
                  switch (newStatus) {
                    case 'qualified': newProb = 25; break;
                    case 'pre-negotiation': newProb = 50; break;
                    case 'negotiation': newProb = 75; break;
                    case 'closed': 
                    case 'paid': newProb = 100; break;
                  }
                  
                  setEditing({ ...editing, status: newStatus, closeProbability: newProb });
                }}
              >
                <option value="qualified">Qualified</option>
                <option value="pre-negotiation">In Talks (Pre-Negotiation)</option>
                <option value="negotiation">In Talks (Negotiation)</option>
                <option value="closed">Closed</option>
                <option value="paid">Paid</option>
                <option value="lost">Lost</option>
              </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-neutral-600">Deal Value</span>
                <input 
                  type="number" 
                  className="border rounded-xl px-3 py-2" 
                  value={editing.dealValue} 
                  onChange={(e) => {
                    const newDealValue = Number(e.target.value);
                    const newCommissionValue = (newDealValue * editing.commissionPercentage) / 100;
                    setEditing({ ...editing, dealValue: newDealValue, commissionValue: newCommissionValue });
                  }} 
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-neutral-600">Commission</span>
                <input 
                  type="number" 
                  className="border rounded-xl px-3 py-2 bg-neutral-50" 
                  value={editing.commissionValue} 
                  readOnly
                />
              </label>
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-neutral-600">Billing Cycle</span>
              <select className="border rounded-xl px-3 py-2" value={editing.billingCycle || ""} onChange={(e) => setEditing({ ...editing, billingCycle: e.target.value })}>
                <option value="">Select billing cycle</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annually">Annually</option>
                <option value="one_time">One Time</option>
              </select>
            </label>
          </div>

          {/* Forecasting */}
          <div className="space-y-3 md:col-span-2">
            <h4 className="font-semibold text-neutral-900 border-b pb-1">Forecasting</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex flex-col gap-1">
                <span className="text-neutral-600">Close Probability (%)</span>
                <div className="flex items-center gap-2">
                  <input 
                    type="range" 
                    min="0" max="100" step="5"
                    className="flex-1"
                    value={editing.closeProbability || 0}
                    onChange={(e) => setEditing({ ...editing, closeProbability: Number(e.target.value) })}
                  />
                  <span className="w-12 text-right tabular-nums">{editing.closeProbability || 0}%</span>
                </div>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-neutral-600">Expected Close Date</span>
                <DatePicker 
                  selected={editing.expectedCloseDate ? (() => {
                     const [y, m, d] = editing.expectedCloseDate.split('-').map(Number);
                     return new Date(y, m - 1, d);
                  })() : null}
                  onChange={(date) => {
                     if (!date) {
                       setEditing({ ...editing, expectedCloseDate: undefined });
                       return;
                     }
                     const y = date.getFullYear();
                     const m = String(date.getMonth() + 1).padStart(2, '0');
                     const d = String(date.getDate()).padStart(2, '0');
                     const dateStr = `${y}-${m}-${d}`;
                     setEditing({ ...editing, expectedCloseDate: dateStr });
                  }}
                  className="w-full border rounded-xl px-3 py-2 text-sm"
                  placeholderText="Select date"
                  dateFormat="yyyy-MM-dd"
                />
              </label>
            </div>
          </div>

          {/* Products */}
          <div className="space-y-3 md:col-span-2">
            <div className="flex items-center justify-between border-b pb-1">
               <h4 className="font-semibold text-neutral-900">Products</h4>
               <select 
                 className="text-xs border rounded-lg px-2 py-1 max-w-[150px]"
                 onChange={(e) => {
                   const [prodId, priceIdx] = e.target.value.split('|');
                   if (prodId) {
                     const product = products.find(p => p.id === prodId);
                     if (product) {
                       const priceInfo = product.prices && product.prices[Number(priceIdx)];
                       const price = priceInfo ? priceInfo.price : product.price;
                       
                       const newDealProduct: DealProduct = {
                         dealId: editing.id,
                         productId: product.id,
                         quantity: 1,
                         priceAtDeal: price,
                         product: product
                       };
                       
                       // Auto-update deal value and billing cycle
                       // We use the new product's price for the deal value
                       // We infer billing cycle from product recurring interval
                       const interval = priceInfo ? priceInfo.recurringInterval : product.recurringInterval;
                       let newBillingCycle = editing.billingCycle;
                       if (interval) {
                         if (interval === 'monthly') newBillingCycle = 'monthly';
                         else if (interval === 'quarterly') newBillingCycle = 'quarterly';
                         else if (interval === 'annually') newBillingCycle = 'annually';
                         else if (interval === 'one_time') newBillingCycle = 'one_time';
                       }

                       // Update Deal Value: Sum of all product prices (existing + new)
                       const currentProductsSum = (editing.products || []).reduce((sum, p) => sum + (p.priceAtDeal * p.quantity), 0);
                       const newDealValue = currentProductsSum + price;
                       
                       // Recalculate commission
                       const newCommissionValue = (newDealValue * editing.commissionPercentage) / 100;

                       setEditing({
                         ...editing,
                         products: [...(editing.products || []), newDealProduct],
                         dealValue: newDealValue,
                         commissionValue: newCommissionValue,
                         billingCycle: newBillingCycle || editing.billingCycle
                       });
                     }
                     e.target.value = "";
                   }
                 }}
               >
                 <option value="">+ Add Product</option>
                 {products.map(p => {
                   if (p.prices && p.prices.length > 0) {
                     return p.prices.map((price, idx) => (
                       <option key={`${p.id}-${idx}`} value={`${p.id}|${idx}`}>
                         {p.name} - {price.label || price.recurringInterval} ({currency(price.price)})
                       </option>
                     ));
                   }
                   return (
                     <option key={p.id} value={`${p.id}|-1`}>{p.name} ({currency(p.price)})</option>
                   );
                 })}
               </select>
            </div>
            
            {(!editing.products || editing.products.length === 0) ? (
              <div className="text-neutral-400 text-center py-2 italic">No products added</div>
            ) : (
              <div className="space-y-2">
                {editing.products.map((dp, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-neutral-50 p-2 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium">{dp.product?.name || "Unknown Product"}</div>
                      <div className="text-xs text-neutral-500">{dp.product?.recurringInterval}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-neutral-500">Qty</label>
                      <input 
                        type="number" 
                        min="1"
                        className="w-16 border rounded px-2 py-1 text-sm"
                        value={dp.quantity}
                        onChange={(e) => updateDealProduct(idx, 'quantity', Number(e.target.value))}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-neutral-500">Price</label>
                      <input 
                        type="number" 
                        className="w-20 border rounded px-2 py-1 text-sm"
                        value={dp.priceAtDeal}
                        onChange={(e) => updateDealProduct(idx, 'priceAtDeal', Number(e.target.value))}
                      />
                    </div>
                    <Button 
                      variant="ghost"
                      size="sm"
                      onClick={() => removeProductFromDeal(idx)}
                      className="text-red-500 hover:bg-red-50 hover:text-red-700 p-1 h-auto w-auto"
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Client Hub Integration */}
          {editing.status === "paid" && (
            <div className="space-y-3 md:col-span-2 border-t pt-4">
              <h4 className="font-semibold text-neutral-900">Client Hub Integration</h4>
              <div className="flex items-center justify-between bg-neutral-50 p-4 rounded-xl border border-neutral-200">
                <div>
                  <div className="font-medium text-sm">Client Account</div>
                  <div className="text-xs text-neutral-500 mt-1">
                    {editing.syncStatus === 'synced' 
                      ? `Linked to Client Hub ID: ${editing.clientHubId}`
                      : "Create a client account in the Client Hub for this deal."}
                  </div>
                </div>
                <div>
                  {editing.syncStatus === 'synced' ? (
                    <ClientHubBadge status="synced" />
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={createClientHubAccount}
                      disabled={isCreatingClient}
                      text={isCreatingClient ? "Creating..." : "Create Client"}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Activity Timeline */}
          <div className="space-y-3 md:col-span-2 border-t pt-4">
            <h4 className="font-semibold text-neutral-900">Activity Timeline & Notes</h4>
            <ActivityTimeline dealId={editing.id} partnerId={partnerId} />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2 border-t pt-4">
          <Button 
            variant="outline"
            onClick={onClose} 
            disabled={saving}
            text="Cancel"
          />
          <Button
            variant="primary"
            onClick={saveDeal}
            disabled={saving}
            text={saving ? "Saving..." : "Save Changes"}
          />
        </div>
      </div>
    </div>
  );
}

